import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShieldCheck, ArrowLeft, Lock, CreditCard, Upload, Link2, Loader2 } from "lucide-react";
import { api, formatApiError, showUploadError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const TIER_LABELS = {
  discover_boost: "Discover Boost",
  boost: "Boost",
  pro_boost: "Pro Boost",
};

const REVIEW_DISCLOSURE =
  "Submitted content is reviewed within 24 hours. Content that doesn't meet our guidelines will be refunded in full.";

const TIERS = [
  {
    id: "discover_boost",
    label: "Discover Boost",
    price: "$4.99",
    period: "for 7 days",
    tag: "Starter",
    tagColor: "text-slate-500 bg-slate-100 border-slate-200",
    features: [
      "Option A — Sponsored Discover page slot",
      "Option B — Leaderboard video placement",
    ],
    note: "Social post link or video — one required per option",
    cta: "pricing-select-discover-boost",
    btnCls: "bg-slate-900 hover:bg-slate-700 text-white",
    featured: false,
  },
  {
    id: "boost",
    label: "Boost",
    price: "$9.99",
    period: "for 10 days",
    tag: "Growth",
    tagColor: "text-slate-600 bg-slate-100 border-slate-200",
    features: [
      "Top Spot page placement",
      "2× sponsored Discover slots (bundled)",
      "Leaderboard video placement",
    ],
    note: "Boosted tag always visible",
    cta: "pricing-select-boost",
    btnCls: "bg-slate-900 hover:bg-slate-700 text-white",
    featured: false,
  },
  {
    id: "pro_boost",
    label: "Pro Boost",
    price: "$14.99",
    period: "for 10 days",
    tag: "Most Popular",
    tagColor: "text-emerald-700 bg-emerald-50 border-emerald-200",
    features: [
      "Everything in Boost",
      "Home screen video placement",
      "HighRank Share Card",
      "Pro Alumni status + priority access",
    ],
    note: "Permanent Pro Alumni badge",
    cta: "pricing-select-pro-boost",
    btnCls: "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_4px_16px_rgba(16,185,129,0.3)]",
    featured: true,
  },
];

/* ── Login gate ──────────────────────────────────────────── */
function LoginPromptStep({ onBack, onNavigate }) {
  return (
    <div className="p-8 text-center" data-testid="boost-login-prompt">
      <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto mb-5">
        <Lock size={26} className="text-slate-500" />
      </div>
      <h3 className="font-display text-3xl uppercase tracking-wide text-slate-900 mb-2">Sign in to continue</h3>
      <p className="text-slate-500 text-sm max-w-xs mx-auto mb-8 leading-relaxed">
        Boosts are tied to your account, so you'll need to sign in before checkout.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          data-testid="boost-login-prompt-signin"
          onClick={() => onNavigate("/auth")}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold text-sm rounded-full px-7 py-3 transition-all"
        >
          Sign in
        </button>
        <button
          data-testid="boost-login-prompt-register"
          onClick={() => onNavigate("/add-business")}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-sm rounded-full px-7 py-3 transition-all"
        >
          Create account
        </button>
      </div>
      <button
        data-testid="boost-login-prompt-back"
        onClick={onBack}
        className="mt-5 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 transition mx-auto"
      >
        <ArrowLeft size={14} /> Back to plans
      </button>
    </div>
  );
}

/* ── Pricing grid ────────────────────────────────────────── */
function PricingStep({ onSelect }) {
  return (
    <div className="p-6 sm:p-8">
      <div className="text-center mb-6">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-emerald-600 mb-2">Visibility Upgrades</p>
        <h2 className="font-display text-3xl uppercase tracking-wide text-slate-900">Choose a Plan</h2>
        <p className="mt-2 text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
          Discover listings are always free. These are optional boosts — none affect your Rank Position.
        </p>
      </div>

      <div className="space-y-3">
        {TIERS.map((t) => (
          <div
            key={t.id}
            className={`rounded-2xl border p-5 transition-all ${
              t.featured
                ? "border-2 border-emerald-400 bg-emerald-50/60 shadow-[0_4px_20px_rgba(16,185,129,0.12)]"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="shrink-0 text-left sm:text-center w-full sm:w-20 flex sm:block items-baseline gap-2">
                <p className={`text-2xl font-black leading-none ${t.featured ? "text-emerald-600" : "text-slate-900"}`}>
                  {t.price}
                </p>
                <p className="text-[10px] text-slate-400 sm:mt-1">{t.period}</p>
              </div>

              <div className="flex-1 min-w-0 w-full">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="font-display text-lg uppercase tracking-wide text-slate-900">{t.label}</span>
                  <span className={`inline-flex items-center font-mono2 text-[9px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${t.tagColor}`}>
                    {t.tag}
                  </span>
                </div>
                <ul className="space-y-1 mb-2">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check size={11} className={`shrink-0 mt-0.5 ${t.featured ? "text-emerald-500" : "text-slate-400"}`} />
                      <span className="text-xs text-slate-600">{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="flex items-center gap-1 text-[10px] text-slate-400">
                  <ShieldCheck size={10} className={t.featured ? "text-emerald-400" : ""} />
                  {t.note}
                </p>
              </div>

              <div className="shrink-0 self-stretch sm:self-center w-full sm:w-auto">
                <button
                  data-testid={t.cta}
                  onClick={() => onSelect(t.id)}
                  className={`w-full sm:w-auto inline-flex items-center justify-center gap-1.5 font-bold text-sm rounded-full px-5 py-2.5 transition-all duration-200 whitespace-nowrap ${t.btnCls}`}
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p data-testid="boost-review-disclosure" className="text-center text-xs text-slate-400 mt-5 leading-relaxed max-w-lg mx-auto">
        {REVIEW_DISCLOSURE}
      </p>
      <div className="flex items-center justify-center gap-1.5 mt-4 opacity-50">
        <Lock size={10} className="text-slate-400" />
        <span className="text-[10px] text-slate-400 tracking-wide">Payments powered by</span>
        <svg viewBox="0 0 512 214" className="h-[14px] w-auto" aria-label="Stripe">
          <path fill="#635bff" d="M512 110.08c0-36.409-17.636-65.138-51.342-65.138-33.85 0-54.33 28.73-54.33 64.854 0 42.808 24.179 64.426 58.88 64.426 16.925 0 29.725-3.84 39.396-9.244v-28.445c-9.67 4.836-20.764 7.823-34.844 7.823-13.796 0-26.027-4.836-27.591-21.618h69.547c0-1.85.284-9.245.284-12.658m-70.258-13.511c0-16.071 9.814-22.756 18.774-22.756 8.675 0 17.92 6.685 17.92 22.756zm-90.31-51.627c-13.939 0-22.899 6.542-27.876 11.094l-1.85-8.818h-31.288v165.83l35.555-7.537.143-40.249c5.12 3.698 12.657 8.96 25.173 8.96 25.458 0 48.64-20.48 48.64-65.564-.142-41.245-23.609-63.716-48.498-63.716m-8.534 97.991c-8.391 0-13.37-2.986-16.782-6.684l-.143-52.765c3.698-4.124 8.818-6.968 16.925-6.968 12.942 0 21.902 14.506 21.902 33.137 0 19.058-8.818 33.28-21.902 33.28M241.493 36.551l35.698-7.68V0l-35.698 7.538zm0 10.809h35.698v124.444h-35.698zm-38.257 10.524L200.96 47.36h-30.72v124.444h35.556V87.467c8.39-10.951 22.613-8.96 27.022-7.396V47.36c-4.551-1.707-21.191-4.836-29.582 10.524m-71.112-41.386-34.702 7.395-.142 113.92c0 21.05 15.787 36.551 36.836 36.551 11.662 0 20.195-2.133 24.888-4.693V140.8c-4.55 1.849-27.022 8.391-27.022-12.658V77.653h27.022V47.36h-27.022zM35.982 83.484c0-5.546 4.551-7.68 12.09-7.68 10.808 0 24.461 3.272 35.27 9.103V51.484c-11.804-4.693-23.466-6.542-35.27-6.542C19.2 44.942 0 60.018 0 85.192c0 39.252 54.044 32.995 54.044 49.92 0 6.541-5.688 8.675-13.653 8.675-11.804 0-26.88-4.836-38.827-11.378v33.849c13.227 5.689 26.596 8.106 38.827 8.106 29.582 0 49.92-14.648 49.92-40.106-.142-42.382-54.329-34.845-54.329-50.774" />
        </svg>
      </div>
    </div>
  );
}

/* ── Content form + pay ──────────────────────────────────── */
function ContentFormStep({ tier, user, company, onBack }) {
  const tierData = TIERS.find((t) => t.id === tier);
  const [businessName, setBusinessName] = useState(company?.name || "");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [socialLink, setSocialLink] = useState("");
  const [socialLink2, setSocialLink2] = useState("");
  const [option, setOption] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [discoverVideoFile, setDiscoverVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);
  const discoverVideoRef = useRef(null);

  const videoOnly = tier === "discover_boost" && option === "option_b";
  const needsVideo = tier === "boost" || tier === "pro_boost" || videoOnly;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessName.trim()) return toast.error("Business name is required");
    if (!contactEmail.trim()) return toast.error("Contact email is required");
    if (tier === "discover_boost" && !option) return toast.error("Choose Option A or Option B");
    if (needsVideo && !videoFile) return toast.error("A video is required for this tier");
    if (!videoOnly && !socialLink.trim() && !videoFile) return toast.error("Add a social post link or upload a video");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("tier", tier);
      fd.append("business_name", businessName.trim());
      fd.append("contact_email", contactEmail.trim());
      fd.append("social_link", socialLink.trim());
      if (socialLink2.trim()) fd.append("social_link_2", socialLink2.trim());
      if (option) fd.append("option_selected", option);
      if (company?.id) fd.append("listing_id", company.id);
      if (videoFile) fd.append("video_file", videoFile);
      if (discoverVideoFile) fd.append("discover_video_file", discoverVideoFile);
      if (thumbnailFile) fd.append("thumbnail_file", thumbnailFile);

      const { data } = await api.post("/boost/create-checkout", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      showUploadError(err);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 sm:p-8" data-testid="boost-content-form">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onBack} data-testid="boost-form-back" className="text-slate-400 hover:text-slate-600 transition">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-xl uppercase tracking-wide text-slate-900">{tierData?.label}</span>
            <span className={`inline-flex items-center font-mono2 text-[9px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${tierData?.tagColor || ""}`}>
              {tierData?.price} · {tierData?.period}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">Fill in your details, then pay securely via Stripe.</p>
        </div>
      </div>

      <div className="space-y-5">
        {/* Business name */}
        <div>
          <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">Business Name</label>
          <input
            data-testid="boost-form-business-name"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Your business name"
            required
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
          />
        </div>

        {/* Contact email */}
        <div>
          <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">Contact Email</label>
          <input
            data-testid="boost-form-contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@company.com"
            required
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
          />
        </div>

        {/* Option A/B for discover_boost */}
        {tier === "discover_boost" && (
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 mb-2">Choose your option</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "option_a", title: "Option A", desc: "Sponsored Discover slot" },
                { id: "option_b", title: "Option B", desc: "Leaderboard video" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`boost-form-${opt.id}`}
                  onClick={() => setOption(opt.id)}
                  className={`text-left rounded-xl border p-3.5 transition-all ${
                    option === opt.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <p className="text-sm font-bold text-slate-800">{opt.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Social link + Thumbnail grouped box for Discover Option A */}
        {tier === "discover_boost" && option === "option_a" ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 space-y-4">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold">Social post + Thumbnail</p>
            <div>
              <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">
                Social Post Link
              </label>
              <div className="relative">
                <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="boost-form-social-link"
                  value={socialLink}
                  onChange={(e) => setSocialLink(e.target.value)}
                  placeholder="https://x.com/yourbrand/status/..."
                  className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-dashed border-emerald-300" />
              <span className="text-xs font-bold text-emerald-500">+</span>
              <div className="flex-1 border-t border-dashed border-emerald-300" />
            </div>
            <div>
              <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">
                Thumbnail Image
              </label>
              <p className="text-[11px] text-slate-500 mb-2">This image will appear on your Discover card. Clicking it takes visitors to your link above.</p>
              <input
                ref={thumbRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="boost-form-thumbnail-input"
                onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
              />
              {thumbnailFile ? (
                <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <img
                    src={URL.createObjectURL(thumbnailFile)}
                    alt="Thumbnail preview"
                    className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                  />
                  <span data-testid="boost-form-thumbnail-name" className="text-sm text-slate-700 truncate flex-1">{thumbnailFile.name}</span>
                  <button type="button" onClick={() => setThumbnailFile(null)} className="text-slate-400 hover:text-slate-600 transition">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="boost-form-thumbnail-button"
                  onClick={() => thumbRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 hover:border-emerald-400 bg-white px-4 py-4 text-sm text-slate-500 hover:text-emerald-600 transition-all"
                >
                  <Upload size={16} /> Choose thumbnail image
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Social link + Thumbnail grouped box — Boost, Pro Boost */}
            {!videoOnly && (tier === "boost" || tier === "pro_boost") && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4 space-y-4">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold">Social post + Thumbnail</p>
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">
                    Social Post Link
                  </label>
                  <div className="relative">
                    <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="boost-form-social-link"
                      value={socialLink}
                      onChange={(e) => setSocialLink(e.target.value)}
                      placeholder="https://x.com/yourbrand/status/..."
                      className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-dashed border-emerald-300" />
                  <span className="text-xs font-bold text-emerald-500">+</span>
                  <div className="flex-1 border-t border-dashed border-emerald-300" />
                </div>
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">
                    Thumbnail Image
                  </label>
                  <p className="text-[11px] text-slate-500 mb-2">This image will appear on your Discover card. Clicking it takes visitors to your link above.</p>
                  <input
                    ref={thumbRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    data-testid="boost-form-thumbnail-input"
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                  />
                  {thumbnailFile ? (
                    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <img
                        src={URL.createObjectURL(thumbnailFile)}
                        alt="Thumbnail preview"
                        className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                      />
                      <span data-testid="boost-form-thumbnail-name" className="text-sm text-slate-700 truncate flex-1">{thumbnailFile.name}</span>
                      <button type="button" onClick={() => setThumbnailFile(null)} className="text-slate-400 hover:text-slate-600 transition">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid="boost-form-thumbnail-button"
                      onClick={() => thumbRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 hover:border-emerald-400 bg-white px-4 py-4 text-sm text-slate-500 hover:text-emerald-600 transition-all"
                    >
                      <Upload size={16} /> Choose thumbnail image
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Social link only — other non-video-only tiers without thumbnail */}
            {!videoOnly && tier !== "boost" && tier !== "pro_boost" && tier !== "discover_boost" && (
              <div>
                <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1.5">
                  Social Post Link
                </label>
                <div className="relative">
                  <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="boost-form-social-link"
                    value={socialLink}
                    onChange={(e) => setSocialLink(e.target.value)}
                    placeholder="https://x.com/yourbrand/status/..."
                    className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Discover Page Video upload — boost & pro_boost bundled Discover slot */}
        {(tier === "boost" || tier === "pro_boost") && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700">
                Discover Page Video <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
              </label>
              <span className="inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-widest text-emerald-600 border border-emerald-200 bg-emerald-50 rounded-full px-2 py-0.5">
                HighRank logo overlay added
              </span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Upload a separate short video for your sponsored Discover page slot. The HighRank logo will be added automatically as a branded watermark.
            </p>
            <input
              ref={discoverVideoRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => setDiscoverVideoFile(e.target.files?.[0] || null)}
            />
            {discoverVideoFile ? (
              <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                <span data-testid="boost-form-discover-video-name" className="text-sm text-slate-700 truncate flex-1">{discoverVideoFile.name}</span>
                <button type="button" onClick={() => setDiscoverVideoFile(null)} className="text-slate-400 hover:text-slate-600 transition">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="boost-form-discover-video-upload"
                onClick={() => discoverVideoRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white hover:border-emerald-400 px-4 py-4 text-sm text-slate-500 transition"
              >
                <Upload size={15} /> Choose Discover video (max 15 MB)
              </button>
            )}
          </div>
        )}

        {/* OR divider for Discover Option A */}
        {tier === "discover_boost" && option === "option_a" && (
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-slate-200" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">or</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>
        )}

        {/* Leaderboard video upload */}
        <div>
          <label className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-700 block mb-1">
            Leaderboard Video Placement {needsVideo ? <span className="text-red-400">(required)</span> : <span className="text-slate-400 normal-case tracking-normal">(optional)</span>}
          </label>
          <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
            This video plays directly on your listing in the leaderboard and company profile page.
          </p>
          <input
            ref={videoRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
          />
          {videoFile ? (
            <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <span data-testid="boost-form-video-name" className="text-sm text-slate-700 truncate flex-1">{videoFile.name}</span>
              <button type="button" onClick={() => setVideoFile(null)} className="text-slate-400 hover:text-slate-600 transition">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="boost-form-video-upload"
              onClick={() => videoRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 hover:border-emerald-400 px-4 py-5 text-sm text-slate-500 transition"
            >
              <Upload size={15} /> Choose a video (max 15 MB)
            </button>
          )}
        </div>
      </div>

      {/* Pay Now */}
      <button
        type="submit"
        data-testid="boost-form-pay-now"
        disabled={submitting}
        className={`w-full mt-6 inline-flex items-center justify-center gap-2 font-bold text-sm rounded-full px-7 py-3.5 transition-all duration-200 ${
          tierData?.featured
            ? "bg-emerald-500 hover:bg-emerald-400 text-white shadow-[0_4px_16px_rgba(16,185,129,0.3)]"
            : "bg-slate-900 hover:bg-slate-700 text-white"
        } disabled:opacity-60`}
      >
        {submitting ? (
          <><Loader2 size={15} className="animate-spin" /> Uploading & creating checkout…</>
        ) : (
          <><CreditCard size={15} /> Pay Now — {tierData?.price}</>
        )}
      </button>

      <p className="text-center text-[11px] text-slate-400 mt-3 leading-relaxed">
        You'll be redirected to Stripe's secure checkout. {REVIEW_DISCLOSURE}
      </p>
      <div className="flex items-center justify-center gap-1.5 mt-3 opacity-50">
        <Lock size={10} className="text-slate-400" />
        <span className="text-[10px] text-slate-400 tracking-wide">Payments powered by</span>
        <svg viewBox="0 0 512 214" className="h-[14px] w-auto" aria-label="Stripe">
          <path fill="#635bff" d="M512 110.08c0-36.409-17.636-65.138-51.342-65.138-33.85 0-54.33 28.73-54.33 64.854 0 42.808 24.179 64.426 58.88 64.426 16.925 0 29.725-3.84 39.396-9.244v-28.445c-9.67 4.836-20.764 7.823-34.844 7.823-13.796 0-26.027-4.836-27.591-21.618h69.547c0-1.85.284-9.245.284-12.658m-70.258-13.511c0-16.071 9.814-22.756 18.774-22.756 8.675 0 17.92 6.685 17.92 22.756zm-90.31-51.627c-13.939 0-22.899 6.542-27.876 11.094l-1.85-8.818h-31.288v165.83l35.555-7.537.143-40.249c5.12 3.698 12.657 8.96 25.173 8.96 25.458 0 48.64-20.48 48.64-65.564-.142-41.245-23.609-63.716-48.498-63.716m-8.534 97.991c-8.391 0-13.37-2.986-16.782-6.684l-.143-52.765c3.698-4.124 8.818-6.968 16.925-6.968 12.942 0 21.902 14.506 21.902 33.137 0 19.058-8.818 33.28-21.902 33.28M241.493 36.551l35.698-7.68V0l-35.698 7.538zm0 10.809h35.698v124.444h-35.698zm-38.257 10.524L200.96 47.36h-30.72v124.444h35.556V87.467c8.39-10.951 22.613-8.96 27.022-7.396V47.36c-4.551-1.707-21.191-4.836-29.582 10.524m-71.112-41.386-34.702 7.395-.142 113.92c0 21.05 15.787 36.551 36.836 36.551 11.662 0 20.195-2.133 24.888-4.693V140.8c-4.55 1.849-27.022 8.391-27.022-12.658V77.653h27.022V47.36h-27.022zM35.982 83.484c0-5.546 4.551-7.68 12.09-7.68 10.808 0 24.461 3.272 35.27 9.103V51.484c-11.804-4.693-23.466-6.542-35.27-6.542C19.2 44.942 0 60.018 0 85.192c0 39.252 54.044 32.995 54.044 49.92 0 6.541-5.688 8.675-13.653 8.675-11.804 0-26.88-4.836-38.827-11.378v33.849c13.227 5.689 26.596 8.106 38.827 8.106 29.582 0 49.92-14.648 49.92-40.106-.142-42.382-54.329-34.845-54.329-50.774" />
        </svg>
      </div>
    </form>
  );
}

/* ── Modal shell ─────────────────────────────────────────── */
export default function BoostPricingModal({ open, onOpenChange, company, initialTier = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("pricing");
  const [selectedTier, setSelectedTier] = useState(null);
  const [myCompany, setMyCompany] = useState(null);

  useEffect(() => {
    if (!company && user) {
      api.get("/companies/mine").then((res) => setMyCompany(res.data)).catch(() => {});
    }
  }, [company, user]);
  const effectiveCompany = company || myCompany;

  const handleSelectTier = (tierId) => {
    if (!user) {
      setSelectedTier(tierId);
      setStep("login_required");
      return;
    }
    setSelectedTier(tierId);
    setStep("form");
  };

  useEffect(() => {
    if (open && initialTier) {
      setSelectedTier(initialTier);
      if (!user) {
        setStep("login_required");
      } else {
        setStep("form");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTier]);

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setStep("pricing"); setSelectedTier(null); }, 300);
  };

  const handleNavigateToAuth = (path) => {
    onOpenChange(false);
    navigate(path, { state: { from: location.pathname + location.search } });
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-4xl bg-white border border-gray-200 rounded-2xl rounded-b-none sm:rounded-b-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              data-testid="boost-pricing-modal-close"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-slate-500 hover:text-slate-800 transition"
            >
              <X size={15} />
            </button>

            {step === "pricing" && <PricingStep onSelect={handleSelectTier} />}
            {step === "form" && selectedTier && (
              <ContentFormStep
                tier={selectedTier}
                user={user}
                company={effectiveCompany}
                onBack={() => { setStep("pricing"); setSelectedTier(null); }}
              />
            )}
            {step === "login_required" && (
              <LoginPromptStep onBack={() => setStep("pricing")} onNavigate={handleNavigateToAuth} />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
